<template>
  <PhotoPreview
    :value="value"
    :title="resolvedTitle"
    @change="onChange"
  >
    <template #content>
      <!-- Figma 2668:61588 / 2668:61626：方形缩略图 + 右侧 180px 资料栏 -->
      <div
        class="br-image-preview"
        :class="{ 'br-image-preview--with-meta': showMetaPanel }"
      >
        <div class="br-image-preview__media">
          <img
            v-if="src"
            :src="src"
            alt=""
            class="br-image-preview__img"
          />
          <button
            v-if="showReupload"
            type="button"
            class="br-image-preview__reupload"
            @click="onReupload"
          >
            {{ $t('lbl_EMPBE_BR_Preview_Reupload') }}
          </button>
        </div>

        <aside v-if="showMetaPanel" class="br-image-preview__meta">
          <div v-if="showStatus" class="br-image-preview__field">
            <span class="br-image-preview__label">{{ $t('lbl_EMPBE_BR_List_Field_Status') }}</span>
            <div class="br-image-preview__value br-image-preview__value--status">
              <BrStatusBadge
                :status-code="badgeStatus"
                :status-name="statusName"
                :label-key="adminBadgeLabelKey"
                :appearance="appearance"
              />
            </div>
          </div>

          <div class="br-image-preview__field">
            <span class="br-image-preview__label">{{ $t('lbl_EMPBE_BR_Preview_Field_RegisteredName') }}</span>
            <p class="br-image-preview__value">{{ companyName }}</p>
          </div>

          <div class="br-image-preview__field">
            <span class="br-image-preview__label">{{ $t('lbl_EMPBE_BR_Preview_Field_RegistrationNo') }}</span>
            <p class="br-image-preview__value">{{ registrationNo }}</p>
          </div>

          <div v-if="validationText" class="br-image-preview__field">
            <span class="br-image-preview__label">{{ $t('lbl_EMPBE_BR_Preview_Field_Validation') }}</span>
            <p class="br-image-preview__value">{{ validationText }}</p>
          </div>

          <div
            v-if="submittedOnText || documentId"
            class="br-image-preview__field br-image-preview__field--footer"
          >
            <div v-if="submittedOnText" class="br-image-preview__field-main">
              <span class="br-image-preview__label">{{ $t('lbl_EMPBE_BR_List_Field_SubmittedOn') }}</span>
              <p class="br-image-preview__value">{{ submittedOnText }}</p>
            </div>
            <span v-if="documentId" class="br-image-preview__id">{{ documentId }}</span>
          </div>
        </aside>
      </div>
    </template>
  </PhotoPreview>
</template>

<script>
import PhotoPreview from '../../../components/PhotoPreview/PhotoPreview.vue';
import BrStatusBadge from './BrStatusBadge.vue';
import {
  formatBrDocumentId,
  formatBrItemValidityText,
  getItemStatusCode,
  resolveBrAdminStatusLabelKey,
  resolveBrBadgeStatus,
  resolveBrItemCompanyName,
  resolveBrItemRegistrationNo,
  resolveBrItemSubmittedOnText,
  resolveBrUiStatus,
} from '../utils/brDocument';

export default {
  name: 'BrImagePreviewModal',
  components: { PhotoPreview, BrStatusBadge },
  props: {
    value: { type: Boolean, default: false },
    src: { type: String, default: '' },
    /** 示例图可传「示例」；真实 BR 预览默认「文件预览」 */
    title: { type: String, default: '' },
    /** 是否在预览图右上角显示「重新上传」 */
    showReupload: { type: Boolean, default: false },
    /** 列表/确认页文档数据；有值时展示右侧资料栏（Figma 2668:61626） */
    item: { type: Object, default: null },
    /** supplier | admin：影响状态徽章样式 */
    appearance: {
      type: String,
      default: 'supplier',
      validator: (value) => ['supplier', 'admin'].includes(value),
    },
  },
  computed: {
    resolvedTitle() {
      if (this.title) {
        return this.title;
      }
      return this.$t('lbl_EMPBE_BR_DocumentPreview');
    },
    showMetaPanel() {
      return !!(this.item && typeof this.item === 'object');
    },
    isAdmin() {
      return this.appearance === 'admin';
    },
    badgeStatus() {
      if (!this.item) {
        return '';
      }
      return this.isAdmin
        ? resolveBrUiStatus(this.item)
        : resolveBrBadgeStatus(this.item);
    },
    statusName() {
      return this.item?.statusName || this.item?.StatusName || '';
    },
    adminBadgeLabelKey() {
      if (!this.isAdmin) {
        return '';
      }
      return resolveBrAdminStatusLabelKey(this.badgeStatus);
    },
    showStatus() {
      return !!(this.badgeStatus || this.statusName || getItemStatusCode(this.item));
    },
    companyName() {
      return resolveBrItemCompanyName(this.item);
    },
    registrationNo() {
      return resolveBrItemRegistrationNo(this.item);
    },
    validationText() {
      return formatBrItemValidityText(this.item, this.$t.bind(this));
    },
    submittedOnText() {
      return resolveBrItemSubmittedOnText(this.item);
    },
    documentId() {
      return formatBrDocumentId(this.item);
    },
  },
  methods: {
    onChange(next) {
      this.$emit('input', next);
    },
    onReupload() {
      this.$emit('input', false);
      this.$emit('reupload');
    },
  },
};
</script>

<style scoped lang="scss">
@import '../styles/_br-tokens.scss';

/* PhotoPreview 壳层需穿透；BEM 样式在 _br-image-preview.scss */
::v-deep .VueModal-Content {
  border-radius: 16px;
  overflow: hidden;
}
::v-deep .VueModal-Header {
  padding: 24px 24px 0;
}
::v-deep .VueModal-Body {
  padding: 16px 24px 24px;
  max-height: calc(100vh - 3rem - 56px);
  overflow: auto;
}
::v-deep .VueModal-Frame {
  background: transparent;
  width: 100%;
  max-width: none;
  margin: 0;
  border-radius: 0;
  overflow: visible;
}
::v-deep .VueModal-Square {
  width: 100%;
  max-width: none;
  max-height: none;
  aspect-ratio: unset;
  height: auto;
  background: transparent;
  border-radius: 0;
  display: block;
  overflow: visible;
}
::v-deep .VueModal-Frame .br-image-preview__img {
  max-width: 100%;
  max-height: 100%;
}
</style>
